"use client";

import { useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Bot,
  Check,
  FileStack,
  FolderTree,
  Layers,
  MonitorSmartphone,
  Play,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import type { LucideIcon } from "lucide-react";
import ServiceFaq from "@/components/services/ServiceFaq";
import ConsultationCTA from "@/components/services/ConsultationCTA";
import StructuredData from "@/components/common/StructuredData";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { getClaudeCoworkContent } from "@/content/claude-cowork-i18n";
import { whatsappHref } from "@/lib/whatsapp";

// Facts compiled from Anthropic's Claude Cowork product page and Help Center
// (accurate July 2026). Reference only — verify against Anthropic's official
// sources. Anchor Sprint is Certified in the Claude Partner Network
// (we build custom Claude agents and enablement), not a reseller of Claude.
//
// All user-visible copy is locale-keyed in @/content/claude-cowork-i18n and
// selected via the `locale` prop, so this component renders fully in English,
// Bahasa Malaysia (/ms), and Simplified Chinese (/zh).

const WHATSAPP_HREF = whatsappHref(
  "Hi Anchor Sprint, I'd like to ask about rolling out Claude Cowork (from the Claude Cowork guide).",
);

type Tool = "chat" | "cowork" | "code";

// Icons for the "What is Cowork" cards and the problems grid are structural, so
// they are kept here and paired positionally with the locale-driven copy.
const PROBLEM_ICONS: ReadonlyArray<LucideIcon> = [FileStack, FolderTree, Layers];
const WHAT_IS_ICONS: ReadonlyArray<LucideIcon> = [MonitorSmartphone, ShieldCheck, Bot];

const EASE = [0.21, 0.6, 0.35, 1] as const;

export default function ClaudeCowork({
  locale = defaultLocale,
}: {
  locale?: Locale;
} = {}) {
  const c = getClaudeCoworkContent(locale);
  const prefersReducedMotion = useReducedMotion();
  const [focus, setFocus] = useState<Tool | "all">("all");
  const [videoPlaying, setVideoPlaying] = useState(false);

  const tools = c.tools;
  const comparison = c.comparison;
  const taskRoutes = c.taskRoutes;

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay = 0) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.55, delay, ease: EASE },
  });

  const inView = (delay = 0) => ({
    initial: prefersReducedMotion ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, delay, ease: EASE },
  });

  const colClass = (tool: Tool) =>
    `px-3 py-3 align-top transition-colors duration-300 ${
      focus === tool ? "bg-primary/5 text-neutral-emphasis" : "text-neutral-muted"
    }`;

  return (
    <div className="bg-background">
      {/* FAQ schema — co-located here so it renders on the English route AND the
          localized /ms and /zh routes (which render this component directly). */}
      <StructuredData
        type="faq"
        faqs={c.faqs.map((f) => ({ question: f.question, answer: f.answer }))}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-background pt-24 pb-16 sm:pt-28 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div>
              <motion.nav
                {...reveal(0)}
                className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-neutral-muted"
              >
                <Link href="/" className="transition-colors hover:text-primary">
                  {c.hero.breadcrumbHome}
                </Link>
                <span className="mx-2" aria-hidden>/</span>
                <span className="text-neutral-emphasis">{c.hero.breadcrumbCurrent}</span>
              </motion.nav>
              <motion.p
                {...reveal(0.06)}
                className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
              >
                {c.hero.eyebrow}
              </motion.p>
              <motion.h1
                {...reveal(0.12)}
                className="max-w-3xl font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-[3.4rem]"
              >
                {c.hero.title}
              </motion.h1>
              <motion.p
                {...reveal(0.2)}
                className="mt-5 max-w-xl text-lg text-neutral-muted"
              >
                {c.hero.introBefore}
                <Link href={getLocalizedPathname("/partners/anthropic-claude-partner/", locale)} className="text-primary underline underline-offset-2 hover:text-blue-800">
                  {c.hero.introLinkLabel}
                </Link>
                {c.hero.introAfter}
              </motion.p>
              <motion.div
                {...reveal(0.28)}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <a
                  href={WHATSAPP_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track-event="contact_whatsapp_click"
                  data-track-location="cowork_guide_hero"
                  data-track-phone="601159246128"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis hover:shadow-lg hover:shadow-primary/20"
                >
                  <FaWhatsapp className="h-4 w-4" aria-hidden />
                  {c.hero.ctaPrimary}
                </a>
                <a
                  href="#compare"
                  className="group inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold text-neutral-emphasis transition hover:border-primary hover:text-primary"
                >
                  {c.hero.ctaSecondary}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
                </a>
              </motion.div>
              <motion.p {...reveal(0.34)} className="mt-5 text-xs text-neutral-muted">
                {c.hero.disclaimerBefore}
                <a
                  href="https://www.anthropic.com/product/claude-cowork"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-primary"
                >
                  {c.hero.disclaimerLinkLabel}
                </a>
                {c.hero.disclaimerAfter}
              </motion.p>
            </div>

            {/* Editorial hero artwork — order emerging from scattered work */}
            <motion.div
              initial={prefersReducedMotion ? false : { scale: 0.96 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.7, delay: 0.18, ease: EASE }}
              className="overflow-hidden rounded-3xl border border-border bg-neutral-surface"
            >
              <img
                src="/images/guides/claude-cowork-hero.jpg"
                alt={c.hero.imageAlt}
                width={1536}
                height={1024}
                className="h-full w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dated correction. Anthropic merged Cowork and chat into one Claude on
          16 Sep 2026, which contradicts this guide's original premise. The page
          holds avg position 8.3, so it is corrected in place rather than pulled.
          Sourced: docs/research/claude-cowork-merged-into-claude.md [CW1-CW5]. */}
      <section className="bg-background pt-4">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-info/30 bg-info/5 p-6">
            <h2 className="font-display text-base font-bold text-neutral-emphasis">
              {c.mergeNotice.heading}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-neutral-muted">
              {c.mergeNotice.body}{" "}
              <a
                href="https://claude.com/blog/cowork-is-now-claude"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline hover:text-blue-800"
              >
                {c.mergeNotice.linkLabel}
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      {/* 01 · What is Cowork */}
      <section className="border-t border-border bg-neutral-surface py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <motion.p
            {...inView()}
            className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted"
          >
            {c.whatIs.eyebrow}
          </motion.p>
          <motion.div {...inView(0.05)} className="space-y-5 text-lg leading-relaxed text-neutral-muted">
            <p>
              <span className="text-neutral-emphasis">{c.whatIs.p1Emphasis}</span>
              {c.whatIs.p1Rest}
            </p>
            <p>
              {c.whatIs.p2a}<span className="text-neutral-emphasis">{c.whatIs.p2Emphasis1}</span>
              {c.whatIs.p2b}
              <span className="text-neutral-emphasis">{c.whatIs.p2Emphasis2}</span>
              {c.whatIs.p2c}
            </p>
          </motion.div>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {c.whatIs.cards.map((item, i) => {
              const Icon = WHAT_IS_ICONS[i];
              return (
                <motion.div
                  key={item.label}
                  {...inView(0.08 * i)}
                  className="rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-sm"
                >
                  <Icon className="h-6 w-6 text-primary" aria-hidden />
                  <p className="mt-3 font-semibold text-neutral-emphasis">{item.label}</p>
                  <p className="mt-1 text-sm text-neutral-muted">{item.note}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* See it in action — official Anthropic video (click-to-load facade) */}
      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView()}>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
              {c.video.eyebrow}
            </p>
            <h2 className="max-w-3xl font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {c.video.title}
            </h2>
            <p className="mt-3 max-w-2xl text-neutral-muted">
              {c.video.body}
            </p>
          </motion.div>

          <motion.div
            {...inView(0.05)}
            className="mt-8 overflow-hidden rounded-3xl border border-border bg-hero-background"
          >
            <div className="relative aspect-video">
              {videoPlaying ? (
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src="https://www.youtube-nocookie.com/embed/zfWfczd6keE?autoplay=1&rel=0"
                  title={c.video.iframeTitle}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setVideoPlaying(true)}
                  aria-label={c.video.playAriaLabel}
                  className="group absolute inset-0 h-full w-full"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="https://i.ytimg.com/vi/zfWfczd6keE/hqdefault.jpg"
                    alt={c.video.facadeImgAlt}
                    className="h-full w-full object-cover opacity-90 transition group-hover:opacity-100"
                    loading="lazy"
                  />
                  <span className="absolute inset-0 bg-neutral-emphasis/25 transition group-hover:bg-neutral-emphasis/15" aria-hidden />
                  <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition group-hover:scale-110" aria-hidden>
                    <Play className="ml-0.5 h-6 w-6 fill-current" />
                  </span>
                </button>
              )}
            </div>
          </motion.div>
          <p className="mt-3 text-xs text-neutral-muted">
            {c.video.creditBefore}
            <a
              href="https://www.youtube.com/watch?v=zfWfczd6keE"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2 hover:text-blue-800"
            >
              {c.video.creditLinkLabel}
            </a>
            {c.video.creditAfter}
          </p>
        </div>
      </section>

      {/* What problem it solves */}
      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.p
            {...inView()}
            className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted"
          >
            {c.problemsSection.eyebrow}
          </motion.p>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {c.problems.map((p, i) => {
              const Icon = PROBLEM_ICONS[i];
              return (
                <motion.div
                  key={p.title}
                  {...inView(0.08 * i)}
                  className="flex h-full flex-col rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-sm"
                >
                  <Icon className="h-7 w-7 text-primary" aria-hidden />
                  <h2 className="mt-4 font-display text-xl font-semibold text-neutral-emphasis">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-muted">{p.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 03 · Interactive comparison — the signature moment */}
      <section id="compare" className="scroll-mt-24 border-y border-border bg-neutral-surface py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView()}>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
              {c.compare.eyebrow}
            </p>
            <h2 className="max-w-3xl font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {c.compare.title}
            </h2>
            <p className="mt-3 max-w-2xl text-neutral-muted">
              {c.compare.body}
            </p>
          </motion.div>

          {/* Interactive focus control — progressive enhancement over static content below */}
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setFocus("all")}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                focus === "all"
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-neutral-muted hover:border-primary hover:text-primary"
              }`}
            >
              {c.compare.showAll}
            </button>
            {tools.map((tool) => (
              <button
                key={tool.id}
                type="button"
                onClick={() => setFocus(tool.id)}
                aria-pressed={focus === tool.id}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                  focus === tool.id
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-neutral-muted hover:border-primary hover:text-primary"
                }`}
              >
                {tool.label}
              </button>
            ))}
          </div>

          <AnimatePresence initial={false} mode="wait">
            {focus !== "all" && (
              <motion.p
                key={focus}
                initial={prefersReducedMotion ? false : { opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: "auto", marginTop: 16 }}
                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="overflow-hidden rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-neutral-emphasis"
              >
                <span className="font-semibold">{tools.find((t) => t.id === focus)?.label}:</span>{" "}
                {tools.find((t) => t.id === focus)?.bestFor}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Comparison table — fully rendered in HTML */}
          <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full min-w-[720px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-border text-neutral-emphasis">
                  <th className="py-4 pl-5 pr-4 font-semibold" scope="col">{c.compare.dimensionHeader}</th>
                  {tools.map((tool) => (
                    <th
                      key={tool.id}
                      scope="col"
                      className={`px-3 py-4 font-semibold transition-colors duration-300 ${
                        focus === tool.id ? "text-primary" : ""
                      }`}
                    >
                      {tool.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.dimension} className="border-b border-border/60 align-top last:border-0">
                    <th scope="row" className="py-3 pl-5 pr-4 font-normal text-neutral-emphasis">
                      {row.dimension}
                    </th>
                    <td className={colClass("chat")}>{row.chat}</td>
                    <td className={colClass("cowork")}>{row.cowork}</td>
                    <td className={colClass("code")}>{row.code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Task router — which tool fits each task */}
          <div className="mt-10">
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.2em] text-neutral-muted">
              {c.compare.taskRouterLabel}
            </p>
            <ul className="grid gap-2 sm:grid-cols-2">
              {taskRoutes.map((item) => {
                const matched = focus !== "all" && focus === item.tool;
                const dimmed = focus !== "all" && focus !== item.tool;
                return (
                  <li
                    key={item.task}
                    className={`flex items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3 text-sm transition-all duration-300 ${
                      matched
                        ? "border-primary/50 shadow-sm"
                        : "border-border"
                    } ${dimmed ? "scale-[0.98] opacity-40" : "opacity-100"}`}
                  >
                    <span className="flex items-center gap-2 text-neutral-emphasis">
                      <AnimatePresence initial={false}>
                        {matched && (
                          <motion.span
                            initial={prefersReducedMotion ? false : { scale: 0, width: 0 }}
                            animate={{ scale: 1, width: "auto" }}
                            exit={prefersReducedMotion ? { opacity: 0 } : { scale: 0, width: 0 }}
                            transition={{ duration: 0.2, ease: EASE }}
                            className="flex-shrink-0 text-primary"
                          >
                            <Check className="h-4 w-4" aria-hidden />
                          </motion.span>
                        )}
                      </AnimatePresence>
                      {item.task}
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium transition-colors duration-300 ${
                        focus === item.tool
                          ? "bg-primary/10 text-primary"
                          : "bg-neutral-surface text-neutral-muted"
                      }`}
                    >
                      {tools.find((t) => t.id === item.tool)?.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* 04 · How to use it */}
      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <motion.p
            {...inView()}
            className="mb-10 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted"
          >
            {c.howTo.eyebrow}
          </motion.p>
          <ol className="relative space-y-8">
            {/* connecting spine */}
            <span
              className="absolute left-4 top-2 bottom-2 w-px -translate-x-1/2 bg-border"
              aria-hidden
            />
            {c.howTo.steps.map((step, i) => (
              <motion.li key={step.title} {...inView(0.06 * i)} className="relative flex gap-5">
                <span className="z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 font-mono text-sm font-semibold text-primary">
                  {i + 1}
                </span>
                <div className="pt-0.5">
                  <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                    {step.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-neutral-muted">{step.body}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* 05 · Use cases for Malaysian teams */}
      <section className="border-t border-border bg-neutral-surface py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView()}>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
              {c.useCasesSection.eyebrow}
            </p>
            <h2 className="max-w-3xl font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {c.useCasesSection.title}
            </h2>
          </motion.div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {c.useCases.map((useCase, i) => (
              <motion.div
                key={useCase.title}
                {...inView(0.06 * i)}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-sm"
              >
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">
                  {useCase.tag}
                </p>
                <h3 className="mt-2 font-display text-lg font-semibold text-neutral-emphasis">
                  {useCase.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{useCase.body}</p>
                <p className="mt-4 rounded-xl border border-border bg-neutral-surface px-4 py-3 font-mono text-[13px] leading-relaxed text-neutral-emphasis">
                  {useCase.prompt}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 06 · Plans, pricing & limits */}
      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <motion.p
            {...inView()}
            className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted"
          >
            {c.pricing.eyebrow}
          </motion.p>
          <motion.div {...inView(0.05)} className="space-y-5 text-lg leading-relaxed text-neutral-muted">
            <p>
              {c.pricing.p1a}<span className="text-neutral-emphasis">{c.pricing.p1Emphasis1}</span>
              {c.pricing.p1b}<span className="text-neutral-emphasis">{c.pricing.p1Emphasis2}</span>
              {c.pricing.p1c}
            </p>
            <p>
              {c.pricing.p2a}
              <Link href="/guides/claude-plans-malaysia/" className="text-primary underline underline-offset-2 hover:text-blue-800">
                {c.pricing.p2LinkLabel1}
              </Link>
              {c.pricing.p2b}
              <Link href="/guides/claude-privacy-compliance-malaysia/" className="text-primary underline underline-offset-2 hover:text-blue-800">
                {c.pricing.p2LinkLabel2}
              </Link>
              {c.pricing.p2c}
            </p>
          </motion.div>
        </div>
      </section>

      {/* 07 · Honest limitations */}
      <section className="border-t border-border bg-neutral-surface py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView()}>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
              {c.limitationsSection.eyebrow}
            </p>
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis">
              {c.limitationsSection.title}
            </h2>
            <p className="mt-3 text-neutral-muted">
              {c.limitationsSection.body}
            </p>
          </motion.div>
          <ul className="mt-6 space-y-3">
            {c.limitations.map((item, i) => (
              <motion.li
                key={item}
                {...inView(0.04 * i)}
                className="flex gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm text-neutral-muted"
              >
                <span className="mt-0.5 text-primary" aria-hidden>—</span>
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* Two conversion paths: team training + custom agents */}
      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView()} className="mx-auto max-w-2xl text-center">
            <p className="mb-3 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
              <Sparkles className="h-4 w-4 text-primary" aria-hidden />
              {c.conversion.eyebrow}
            </p>
            <h2 className="font-display text-3xl font-bold text-neutral-emphasis md:text-4xl">
              {c.conversion.title}
            </h2>
            <p className="mt-4 text-lg text-neutral-muted">
              {c.conversion.body}
            </p>
          </motion.div>

          <div className="mt-12 grid gap-5 lg:grid-cols-2">
            {/* Offer 1 — training & enablement (light card) */}
            <motion.div
              {...inView(0.05)}
              className="flex h-full flex-col rounded-3xl border border-border bg-card p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-sm lg:p-10"
            >
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">
                {c.conversion.training.eyebrow}
              </p>
              <h3 className="mt-3 font-display text-2xl font-bold text-neutral-emphasis">
                {c.conversion.training.title}
              </h3>
              <p className="mt-3 text-neutral-muted">
                {c.conversion.training.body}
              </p>
              <ul className="mt-5 space-y-2 text-sm text-neutral-muted">
                {c.conversion.training.points.map((point) => (
                  <li key={point} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-8">
                <a
                  href={WHATSAPP_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track-event="contact_whatsapp_click"
                  data-track-location="cowork_guide_training"
                  data-track-phone="601159246128"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis hover:shadow-lg hover:shadow-primary/20"
                >
                  <FaWhatsapp className="h-4 w-4" aria-hidden />
                  {c.conversion.training.cta}
                </a>
              </div>
            </motion.div>

            {/* Offer 2 — custom agents (dark proof-moment card) */}
            <motion.div
              {...inView(0.1)}
              className="flex h-full flex-col rounded-3xl bg-hero-background p-8 lg:p-10"
            >
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary-contrast/60">
                {c.conversion.agents.eyebrow}
              </p>
              <h3 className="mt-3 font-display text-2xl font-bold text-primary-contrast">
                {c.conversion.agents.title}
              </h3>
              <p className="mt-3 text-primary-contrast/70">
                {c.conversion.agents.body}
              </p>
              <ul className="mt-5 space-y-2 text-sm text-primary-contrast/70">
                {c.conversion.agents.points.map((point) => (
                  <li key={point} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-contrast/80" aria-hidden />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto flex flex-col gap-3 pt-8 sm:flex-row">
                <a
                  href={WHATSAPP_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track-event="contact_whatsapp_click"
                  data-track-location="cowork_guide_agents"
                  data-track-phone="601159246128"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis hover:shadow-lg hover:shadow-primary/30"
                >
                  <FaWhatsapp className="h-4 w-4" aria-hidden />
                  {c.conversion.agents.ctaPrimary}
                </a>
                <Link
                  href="/services/ai-solutions/"
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-primary-contrast/20 px-7 py-3 text-sm font-semibold text-primary-contrast transition hover:border-primary-contrast/40 hover:bg-primary-contrast/10"
                >
                  {c.conversion.agents.ctaSecondary}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border bg-neutral-surface py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <motion.p
            {...inView()}
            className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted"
          >
            {c.faqSection.eyebrow}
          </motion.p>
          <ServiceFaq items={c.faqs.map((f) => ({ question: f.question, answer: f.answer }))} />
        </div>
      </section>

      {/* Keep reading */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
            {c.keepReading.eyebrow}
          </p>
          <div className="flex flex-wrap gap-x-8 gap-y-4">
            <Link href="/guides/claude-plans-malaysia/" className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-blue-800">
              {c.keepReading.links[0]} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
            </Link>
            <Link href="/guides/claude-privacy-compliance-malaysia/" className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-blue-800">
              {c.keepReading.links[1]} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
            </Link>
            <Link href="/partners/anthropic-claude-partner/" className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-blue-800">
              {c.keepReading.links[2]} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
            </Link>
            <Link href="/services/ai-solutions/" className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-blue-800">
              {c.keepReading.links[3]} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-background pb-16 lg:pb-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-border bg-neutral-surface p-6 text-sm text-neutral-muted">
            <p className="mb-2 font-mono text-xs uppercase tracking-[0.2em] text-neutral-emphasis">
              {c.disclaimer.label}
            </p>
            <p>
              {c.disclaimer.before}
              <a href="https://www.anthropic.com/product/claude-cowork" className="text-primary underline underline-offset-2 hover:text-blue-800" target="_blank" rel="noopener noreferrer">
                {c.disclaimer.linkLabel}
              </a>
              {c.disclaimer.after}
            </p>
          </div>
        </div>
      </section>

      <ConsultationCTA
        title={c.consultation.title}
        description={c.consultation.description}
        buttonText={c.consultation.buttonText}
      />
    </div>
  );
}
